import React, { useRef, useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../common/AddNoticesAndLitigationPanel.css";
import { toast } from "../../../../utils/toast";
import PageLoader from "../../../shared/PageLoader";
import cloudUpload from "./../../../../assets/img/icon/cloudUpload.svg";
import { getUserDetails } from "../../../../backend/auth";
import { FULL_USER_COMPANIES_PAYLOAD, useCreateNotice, useGetNoticeCategories, useCreateCategory } from "../../../../backend/notice";
import { useGetUserCompanies } from "../../../../backend/query";
import { useGetUsers } from "../../../../backend/users";
import { useGetActs } from "../../../../backend/masters";
import { get, post } from "../../../../backend/request";
import { parseS3Url } from "../../../../utils/common";
import { DropdownIndicator, CustomMenuList } from "../common/TableComponents";

interface SidePanelProps{
  isOpen: boolean;
  closePanel: () => void;
  onSuccess?: () => void;
}

const AddNewNoticesPanel: React.FC<SidePanelProps> = ({
  isOpen,
  closePanel,
  onSuccess,
}) => {
  const user = getUserDetails();
  const [currentStep, setCurrentStep] = useState(1);

  // Form state
  const [formData, setFormData] = useState<any>({
    company_id: "",
    associate_company_id: "",
    location_id: "",
    notice_name: "",
    description: "",
    category_id: "",
    issued_date: "",
    resolution_date: "",
    issued_by: "",
    act_id: "",
    assigned_to: "",
    watcher_ids: [],
    status: "active",
  });

  // API hooks for dropdowns
  const { userCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  const [companies, setCompanies] = useState<any[]>([]);
  const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [hasDuplicate, setHasDuplicate] = useState<boolean>(false);
  const resolutionDatePickerRef = useRef<any>(null);
  const panelBodyRef = useRef<HTMLDivElement>(null);
  const sidePanelRef = useRef<HTMLDivElement>(null);


  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      company_id: "",
      associate_company_id: "",
      location_id: "",
      notice_name: "",
      description: "",
      category_id: "",
      issued_date: "",
      resolution_date: "",
      issued_by: "",
      act_id: "",
      assigned_to: "",
      watcher_ids: [],
      status: "active",
    });
    setAssociateCompanies([]);
    setLocations([]);
    setUploadedFiles([]);
    setUploadedDocuments([]);
  };

  // Handle close panel with reset
  const handleClosePanel = () => {
    resetForm();
    closePanel();
  };

  // Scroll to top when panel opens
  useEffect(() => {
    if (isOpen) {
      // Add small delay to wait for panel animation
      setTimeout(() => {
        if (panelBodyRef.current) {
          panelBodyRef.current.scrollTop = 0;
        }
        if (sidePanelRef.current) {
          sidePanelRef.current.scrollTop = 0;
        }
      }, 100);
    }
  }, [isOpen]);

  // Process companies
  useEffect(() => {
    if (userCompanies && userCompanies.length > 0) {
      setCompanies(userCompanies);
    }
  }, [userCompanies]);

  // Cascade: Company → Associate Companies
  useEffect(() => {
    if (formData.company_id && companies.length > 0) {
      const selectedCompany = companies.find(
        (c: any) => c.id === formData.company_id
      );
      if (selectedCompany && selectedCompany.associate_companies) {
        setAssociateCompanies(selectedCompany.associate_companies);
      } else {
        setAssociateCompanies([]);
      }
      setFormData((prev: any) => ({
        ...prev,
        associate_company_id: "",
        location_id: "",
      }));
      setLocations([]);
    } else {
      setAssociateCompanies([]);
    }
  }, [formData.company_id, companies]);

  // Cascade: Associate Company → Locations
  useEffect(() => {
    if (formData.associate_company_id && associateCompanies.length > 0) {
      const selectedAssocCompany = associateCompanies.find(
        (ac: any) => ac.id === formData.associate_company_id
      );
      if (selectedAssocCompany && selectedAssocCompany.locations) {
        setLocations(selectedAssocCompany.locations);
      } else {
        setLocations([]);
      }
      setFormData((prev: any) => ({
        ...prev,
        location_id: "",
      }));
    } else {
      setLocations([]);
    }
  }, [formData.associate_company_id, associateCompanies]);

  // Live duplicate validation when act is selected
  useEffect(() => {
    const checkDuplicate = async () => {
      if (
        formData.company_id &&
        formData.associate_company_id &&
        formData.location_id &&
        formData.notice_name &&
        formData.act_id
      ) {
        try {
          const params = new URLSearchParams();
          params.append('filters[company_id_eq]', formData.company_id);
          params.append('filters[associate_company_id_eq]', formData.associate_company_id);
          params.append('filters[location_id_eq]', formData.location_id);
          params.append('filters[name_eq]', formData.notice_name);
          params.append('filters[act_id_eq]', formData.act_id);
          params.append('filters[status_eq]', 'open');

          const response = await get(`/api/v1/notice_management/notices?${params.toString()}`);
          const totalNotices = response && response.data && response.data.meta && response.data.meta.counts && response.data.meta.counts.total_notices;

          if (totalNotices > 0) {
            setHasDuplicate(true);
            // toast.error("An active notice with the same name already exists for this company and act.");
          } else {
            setHasDuplicate(false);
          }
        } catch (error) {
          console.error("Error checking for duplicate notices:", error);
          setHasDuplicate(false);
        }
      } else {
        setHasDuplicate(false);
      }
    };

    checkDuplicate();
  }, [formData.act_id, formData.company_id, formData.notice_name, formData.associate_company_id, formData.location_id]);

  // Category management
  const { categories: fetchedCategories, refetch: refetchCategories } = useGetNoticeCategories(
    formData.company_id,
    !!formData.company_id
  );
  const categories = fetchedCategories || [];
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  // Auto-select latest category
  useEffect(() => {
    if (categories.length > 0 && !formData.category_id) {
      setFormData((prev: any) => ({
        ...prev,
        category_id: categories[0].id,
      }));
    }
  }, [categories]);

  const { createCategory, creating: creatingCategory } = useCreateCategory(
    (data: any) => {
      toast.success("Category added successfully");
      const newCategory = data && data.data ? data.data : null;
      if (newCategory && newCategory.id) {
        setFormData((prev: any) => ({
          ...prev,
          category_id: newCategory.id,
        }));
      }
      refetchCategories();
      setNewCategoryName("");
      setShowCategoryPopup(false);
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessage =
        (errorData && errorData.error_messages && errorData.error_messages[0]) ||
        (errorData && errorData.message) ||
        "Failed to create category";
      toast.error(errorMessage);
    }
  );

  // Fetch Acts only when panel is open
  const { acts = [] } = useGetActs({}, isOpen);

  // Fetch Notice Owners (users with Notice Owner role)
  const { records: fetchedNoticeOwners = [] } = useGetUsers(
    {
      filters: {
        companies_id_in: [formData.associate_company_id],
        locations_id_in: [formData.location_id],
        roles_name_in: ["Notice Owner"],
      },
    },
    Boolean(formData.associate_company_id && formData.location_id)
  );

  // Deduplicate owners by ID
  const noticeOwners = Array.from(
    new Map(fetchedNoticeOwners.map((owner: any) => [owner.id, owner])).values()
  );

  // Fetch Notice Watchers (users with Notice Watcher role)
  const { records: fetchedNoticeWatchers = [] } = useGetUsers(
    {
      filters: {
        companies_id_in: [formData.associate_company_id],
        locations_id_in: [formData.location_id],
        roles_name_in: ["Notice Watcher"],
      },
    },
    Boolean(formData.associate_company_id && formData.location_id)
  );

  // Deduplicate watchers by ID
  const noticeWatchers = Array.from(
    new Map(fetchedNoticeWatchers.map((watcher: any) => [watcher.id, watcher])).values()
  );

  // File upload
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const { createNotice, creating } = useCreateNotice(
    () => {
      toast.success("Notice created successfully");
      resetForm();
      closePanel();
      if (onSuccess) onSuccess();
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessage =
        (errorData && errorData.details && errorData.details[0]) ||
        (errorData && errorData.error) ||
        (errorData && errorData.error_messages && errorData.error_messages[0]) ||
        (errorData && errorData.message) ||
        "Failed to create notice";
      toast.error(errorMessage);
    }
  );


  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleIssuedDateChange = (value: string) => {
    handleInputChange("issued_date", value);
    if (value && resolutionDatePickerRef.current) {
      setTimeout(() => {
        if (resolutionDatePickerRef.current) {
          resolutionDatePickerRef.current.setOpen(true);
        }
      }, 100);
    }
  };

  const handleCategoryChange = (value: string) => {
    if (value === "add_new") {
      setShowCategoryPopup(true);
      setFormData((prev: any) => ({ ...prev, category_id: "" }));
    } else {
      handleInputChange("category_id", value);
    }
  };

  const handleSaveCategory = () => {
    if (!newCategoryName || newCategoryName.trim() === "") {
      toast.error("Please add category name");
      return;
    }
    if (!formData.company_id) {
      toast.error("Please select a company first");
      return;
    }

    const payload = {
      companyId: formData.company_id,
      name: newCategoryName.trim(),
      category_type: "notice",
      company_id: formData.company_id,
    };

    createCategory(payload as any);
  };

  const handleCloseCategoryPopup = () => {
    setNewCategoryName("");
    setShowCategoryPopup(false);
  };

  const validateStep1 = () => {
    if (!formData.notice_name || formData.notice_name.trim() === "") {
      toast.error("Please add notice name");
      return false;
    }
    if (formData.notice_name.length > 250) {
      toast.error("Notice name cannot exceed 250 characters");
      return false;
    }
    if (!formData.description || formData.description.trim() === "") {
      toast.error("Please add description");
      return false;
    }
    if (formData.description.length > 1000) {
      toast.error("Description cannot exceed 1000 characters");
      return false;
    }
    if (!formData.issued_date) {
      toast.error("Please select issued date");
      return false;
    }
    if (!formData.resolution_date) {
      toast.error("Please select resolution date");
      return false;
    }
    if (new Date(formData.resolution_date) <= new Date(formData.issued_date)) {
      toast.error("Resolution date must be greater than issued date");
      return false;
    }
    if (!formData.issued_by || formData.issued_by.trim() === "") {
      toast.error("Please add issued by");
      return false;
    }
    if (formData.issued_by.length > 250) {
      toast.error("Issued by cannot exceed 250 characters");
      return false;
    }
    if (!formData.company_id) {
      toast.error("Please select a company");
      return false;
    }
    if (!formData.associate_company_id) {
      toast.error("Please select an associate company");
      return false;
    }
    if (!formData.location_id) {
      toast.error("Please select a location");
      return false;
    }
    if (!formData.category_id) {
      toast.error("Please select a category");
      return false;
    }
    if (!formData.act_id) {
      toast.error("Please select issued under act");
      return false;
    }
    if (!formData.assigned_to) {
      toast.error("Please assign to a user");
      return false;
    }
    if (!formData.watcher_ids.length) {
      toast.error("Watcher field is mandatory");
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (hasDuplicate) {
      toast.error("An active notice with the same name already exists for this company and act.");
      return;
    }
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };


  const handleDownload = (file: File) => {
    try {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      }, 1000);
    } catch (err) {
      toast.error("Unable to download file");
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const validExtensions = ["jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx", "pdf"];
    const currentFileCount = uploadedFiles.length;
    const newFileCount = files.length;
    const totalFileCount = currentFileCount + newFileCount;

    if (totalFileCount > 5) {
      toast.error(`You can upload a maximum of 5 files. Currently ${currentFileCount} file${currentFileCount !== 1 ? 's' : ''} uploaded.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    files.forEach((file) => {
      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File "${file.name}" exceeds 5MB limit`);
        return;
      }

      // Check file extension
      const parts = file.name.split(".");
      const extension = parts.length > 0 ? parts[parts.length - 1].toLowerCase() : "";

      if (!extension || !validExtensions.includes(extension)) {
        invalidFiles.push(file.name);
        return;
      }

      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Invalid file type. Supported formats: jpg, jpeg, png, doc, docx, xls, xlsx, pdf`);
    }

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      setIsUploading(true);

      try {
        const uploadPromises = validFiles.map(async (file) => {
          const uploadFormData = new FormData();
          uploadFormData.append("file", file);
          uploadFormData.append("resource_type", "notice");
          uploadFormData.append("file_type", "notice/document");
          uploadFormData.append("resource_field", "notice_document");
          uploadFormData.append("skip_deep_scan", "true");

          const response = await post(
            "/api/v1/upload",
            uploadFormData,
            { "Content-Type": "multipart/form-data" },
            true
          );

          const responseData = response && response.data;
          if (responseData && responseData.url) {
            const fileInfo = responseData.file_info || {};
            const uploadedUrl = responseData.url;

            let s3Key = "";
            let s3Bucket = "";
            let s3Region = "";

            const s3Params = parseS3Url(uploadedUrl);
            if (s3Params) {
              s3Bucket = s3Params.s3_bucket;
              s3Region = s3Params.s3_region;
              s3Key = s3Params.s3_key;
            }

            return {
              asset_type: "notice_document",
              name: fileInfo.filename || file.name,
              s3_key: s3Key,
              s3_bucket: s3Bucket,
              s3_region: s3Region,
              file_path: uploadedUrl,
              content_type: fileInfo.content_type || file.type,
              file_size: fileInfo.size || file.size,
              status: "active",
              uploaded_by_id: user.user_id,
              url: uploadedUrl,
              file_info: fileInfo,
            };
          }
          return null;
        });

        const newDocuments = await Promise.all(uploadPromises);
        const validDocuments = newDocuments.filter((doc) => doc !== null);
        setUploadedDocuments((prev) => [...prev, ...validDocuments]);
      } catch (error) {
        toast.error("Failed to upload files");
      } finally {
        setIsUploading(false);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadedDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onSubmit = async () => {
    const documentsAttributes: any[] = uploadedDocuments;

    const payload = {
      notice: {
        company_id: formData.company_id,
        associate_company_id: formData.associate_company_id,
        location_id: formData.location_id,
        name: formData.notice_name,
        description: formData.description,
        category_id: formData.category_id,
        issued_date: formData.issued_date,
        resolution_date: formData.resolution_date,
        issued_by: formData.issued_by,
        act_id: formData.act_id,
        assigned_to: formData.assigned_to || null,
        watcher_ids: formData.watcher_ids,
        status: formData.status,
        created_by_id: user.user_id,
        updated_by_id: user.user_id,
      },
      document_details: documentsAttributes,
    };

    createNotice(payload as any);
  };

  return (
    <>
      {isOpen && <div className="sidepanel-overlay"></div>}
      <div className="sidepanel-wrapper">
        <div ref={sidePanelRef} className={`sidepanel ${isOpen ? "open" : ""}`}>
          <div className="sidepanel-header d-flex justify-content-between align-items-center pb-4">
            <h5 className="mb-0 fw-semibold">Add New Notice</h5>
            <button
              className="btn-close"
              aria-label="Close"
              onClick={handleClosePanel}
            ></button>
          </div>

          <div ref={panelBodyRef} className="sidepanel-body h-100">
            <ul className="progress-section p-0 m-0 pl-2">
              <li
                className={currentStep === 1 || currentStep > 1 ? "active" : ""}
              >
                <div className="d-flex align-items-center">
                  <div className="progress-circle d-flex justify-content-center align-items-center">
                    <div
                      className={`progress-checked ${
                        currentStep > 1 ? "completed" : ""
                      }`}
                    ></div>
                  </div>
                  <div className="progress-line"></div>
                </div>
                <p>Step 1</p>
                <h6>Notice Details</h6>
              </li>
              <li
                className={currentStep === 2 || currentStep > 2 ? "active" : ""}
              >
                <div className="d-flex align-items-center">
                  <div className="progress-circle d-flex justify-content-center align-items-center">
                    <div
                      className={`progress-checked ${
                        currentStep > 2 ? "completed" : ""
                      }`}
                    ></div>
                  </div>
                </div>
                <p>Step 2</p>
                <h6>Upload Documents</h6>
              </li>
            </ul>

            <hr className="my-4" />

            {/* Step 1 - Basic Details */}
            {currentStep === 1 && (
              <form>
                <div className="mb-3">
                  <label className="form-label">
                    Notice Name<span>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Add notice name"
                    maxLength={64}
                    value={formData.notice_name}
                    onChange={(e) =>
                      handleInputChange("notice_name", e.target.value)
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Description<span>*</span>
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Add description here"
                    maxLength={1000}
                    rows={4}
                    style={{minHeight: "114px"}}
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                  ></textarea>
                  <small className="text-muted text-sm d-flex justify-content-end">
                    {formData.description.length}/1000 characters
                  </small>
                </div>

                <div className="d-flex gap-3">
                  <div className="mb-3 w-50">
                    <label className="form-label">
                      Issued Date<span>*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <DatePicker
                        selected={formData.issued_date ? new Date(formData.issued_date) : null}
                        onChange={(date: Date | null) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            handleIssuedDateChange(`${year}-${month}-${day}`);
                          } else {
                            handleIssuedDateChange("");
                          }
                        }}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select date"
                        className="form-control"
                        wrapperClassName="w-100"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={50}
                        maxDate={formData.resolution_date ? new Date(formData.resolution_date) : null}
                      />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#1E1E1E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none'
                        }}
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    </div>
                  </div>

                  <div className="mb-3 w-50">
                    <label className="form-label">
                      Resolution Date<span>*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <DatePicker
                        ref={resolutionDatePickerRef}
                        selected={formData.resolution_date ? new Date(formData.resolution_date) : null}
                        onChange={(date: Date | null) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            handleInputChange("resolution_date", `${year}-${month}-${day}`);
                          } else {
                            handleInputChange("resolution_date", "");
                          }
                        }}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select date"
                        className="form-control"
                        wrapperClassName="w-100"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={50}
                        minDate={formData.issued_date ? new Date(formData.issued_date) : null}
                        disabled={!formData.issued_date}
                      />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#1E1E1E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none'
                        }}
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-3">
                  <div className="mb-3 w-50">
                    <label className="form-label">
                      Issued By<span>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add issued by"
                      maxLength={64}
                      value={formData.issued_by}
                      onChange={(e) =>
                        handleInputChange("issued_by", e.target.value)
                      }
                    />
                  </div>

                  <div className="mb-3 w-50">
                    <label className="form-label">
                      Company<span>*</span>
                    </label>
                    <Select
                      placeholder="Select company"
                      options={(companies || []).map((company: any) => ({
                        value: company.id,
                        label: company.name
                      }))}
                      onChange={(option: any) =>
                        handleInputChange("company_id", option ? option.value : "")
                      }
                      value={(companies || []).map((c: any) => ({ value: c.id, label: c.name })).find((c: any) => c.value === formData.company_id) || null}
                      className="select-control"
                      components={{ DropdownIndicator }}
                      isSearchable={true}
                      isClearable={false}
                    />
                  </div>
                </div>

                <div className="d-flex gap-3">
                  <div className="mb-3 w-50">
                    <label className="form-label">
                      Associate Company<span>*</span>
                    </label>
                    <Select
                      placeholder="Select associate company"
                      options={(associateCompanies || []).map((company: any) => ({
                        value: company.id,
                        label: company.name
                      }))}
                      onChange={(option: any) =>
                        handleInputChange("associate_company_id", option ? option.value : "")
                      }
                      value={(associateCompanies || []).map((c: any) => ({ value: c.id, label: c.name })).find((c: any) => c.value === formData.associate_company_id) || null}
                      className="select-control"
                      components={{ DropdownIndicator }}
                      isDisabled={!formData.company_id}
                      isSearchable={true}
                      isClearable={false}
                    />
                  </div>

                  <div className="mb-3 w-50">
                    <label className="form-label">
                      Location<span>*</span>
                    </label>
                    <Select
                      placeholder="Select location"
                      options={(locations || []).map((location: any) => ({
                        value: location.id,
                        label: location.name
                      }))}
                      onChange={(option: any) =>
                        handleInputChange("location_id", option ? option.value : "")
                      }
                      value={(locations || []).map((l: any) => ({ value: l.id, label: l.name })).find((l: any) => l.value === formData.location_id) || null}
                      className="select-control"
                      components={{ DropdownIndicator }}
                      isDisabled={!formData.associate_company_id}
                      isSearchable={true}
                      isClearable={false}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Category<span>*</span>
                  </label>
                  <Select
                    placeholder="Select category"
                    options={[
                      ...(categories || []).map((category: any) => ({
                        value: category.id,
                        label: category.name
                      })),
                      { value: "add_new", label: "+ Add new category" }
                    ]}
                    onChange={(option: any) => handleCategoryChange(option ? option.value : "")}
                    value={[
                      ...(categories || []).map((c: any) => ({ value: c.id, label: c.name })),
                      { value: "add_new", label: "+ Add new category" }
                    ].find((c: any) => c.value === formData.category_id) || null}
                    className="select-control"
                    components={{ DropdownIndicator, MenuList: CustomMenuList }}
                    isSearchable={true}
                    isClearable={false}
                    isDisabled={!formData.location_id}
                  />
                </div>

                <div className="d-flex gap-3">
                  <div className="mb-3 w-50">
                    <label className="form-label">
                      Issued Under Act<span>*</span>
                    </label>
                    <Select
                      placeholder="Select issued under act"
                      options={(acts || []).map((act: any) => ({
                        value: act.id,
                        label: `${act.establishment_type} - ${act.name}`
                      }))}
                      onChange={(option: any) =>
                        handleInputChange("act_id", option ? option.value : "")
                      }
                      value={(acts || []).map((a: any) => ({ value: a.id, label: `${a.establishment_type} - ${a.name}` })).find((a: any) => a.value === formData.act_id) || null}
                      className="select-control"
                      components={{ DropdownIndicator }}
                      isSearchable={true}
                      isClearable={false}
                    />
                  </div>

                  <div className="mb-3 w-50">
                    <label className="form-label">
                      Assigned To<span>*</span>
                    </label>
                    <Select
                      placeholder="Select assigned to"
                      options={(noticeOwners || []).map((owner: any) => ({
                        value: owner.id,
                        label: owner.name
                      }))}
                      onChange={(option: any) =>
                        handleInputChange("assigned_to", option ? option.value : "")
                      }
                      value={(noticeOwners || []).map((o: any) => ({ value: o.id, label: o.name })).find((o: any) => o.value === formData.assigned_to) || null}
                      className="select-control"
                      components={{ DropdownIndicator }}
                      isDisabled={!formData.location_id}
                      isSearchable={true}
                      isClearable={false}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Watchers<span>*</span>
                  </label>
                  <div
                    className="watchers-box"
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      backgroundColor: (!formData.location_id || noticeWatchers.length === 0) ? '#f2f2f2' : 'transparent',
                      pointerEvents: (!formData.location_id || noticeWatchers.length === 0) ? 'none' : 'auto',
                      padding: (!formData.location_id || noticeWatchers.length === 0) ? '9px 8px' : '16px',
                    }}
                  >
                    {(!formData.location_id || noticeWatchers.length === 0) ? (
                      <div style={{ fontSize: "14px", color: '#808080' }}>Select watcher</div>
                    ) : (
                      (noticeWatchers || []).map((watcher: any) => (
                        <div
                          key={watcher.id}
                          className="mb-3 d-flex gap-2 align-items-start"
                        >
                          <input
                            className="form-check"
                            type="checkbox"
                            id={`watcher-${watcher.id}`}
                            checked={formData.watcher_ids.includes(watcher.id)}
                            disabled={!formData.location_id}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData((prev: any) => ({
                                ...prev,
                                watcher_ids: checked
                                  ? [...prev.watcher_ids, watcher.id]
                                  : prev.watcher_ids.filter(
                                      (id: string) => id !== watcher.id
                                    ),
                              }));
                            }}
                          />
                          <label
                            htmlFor={`watcher-${watcher.id}`}
                            style={{ cursor: "pointer" }}
                          >
                            {watcher.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {hasDuplicate && (
                  <div className="alert alert-danger mb-3" role="alert" style={{ fontSize: '14px' }}>
                    <strong>Active Notice Exists:</strong> An active notice with the same name and same act already exists for this company.
                  </div>
                )}
              </form>
            )}

            {/* Step 2 - Upload Documents */}
            {currentStep === 2 && (
              <form>
                <div className="mb-3">
                  <label className="form-label">Upload Notice Documents</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.pdf"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                    multiple
                  />

                  {uploadedFiles.length < 5 && (
                    <div
                      className="p-4 text-center upload-box mb-3"
                      onClick={handleChooseFile}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="mb-2">
                        <img
                          src={cloudUpload}
                          alt="cloudUpload"
                          className="me-2"
                        />
                      </div>
                      <p className="mb-2 text-md">
                        Click to browse and upload
                      </p>
                      <button
                        type="button"
                        className="mb-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChooseFile();
                        }}
                      >
                        Choose File
                      </button>
                      <div>
                        <small className="text-gray-12 d-block">
                          Supports up to 5 files (max 5 MB each): JPG, JPEG, PNG, DOC, DOCX, XLS, XLSX, PDF
                        </small>
                      </div>
                    </div>
                  )}

                  {uploadedFiles.length > 0 &&
                    uploadedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="upload-successful position-relative mb-3"
                      >
                        <div
                          className="close-button position-absolute"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(index);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z"
                              stroke="#757575"
                              strokeWidth="1.33333"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="p-4 d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2" style={{width: "90%"}}>
                            <p className="text-sm m-0 ellipse">{file.name}</p>
                          </div>
                          <div
                            style={{
                              cursor: "pointer",
                              height: "25px",
                              width: "25px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(file);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M20.9902 15V19C20.9902 19.5304 20.7795 20.0391 20.4044 20.4142C20.0294 20.7893 19.5207 21 18.9902 21H4.99023C4.4598 21 3.95109 20.7893 3.57602 20.4142C3.20095 20.0391 2.99023 19.5304 2.99023 19V15M6.99023 10L11.9902 15M11.9902 15L16.9902 10M11.9902 15V3"
                                stroke="#2965AD"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </form>
            )}

          </div>
        </div>

        <div className={`button-container ${isOpen ? "open" : ""}`}>
          {currentStep === 1 && (
            <div className="w-100">
              <button
                type="button"
                className="button-primary w-100"
                onClick={handleNextStep}
                disabled={hasDuplicate}
              >
                Next
              </button>
            </div>
          )}
          {currentStep === 2 && (
            <div className="d-flex gap-3 w-100">
              <button
                type="button"
                className="button-secondary w-100"
                onClick={handlePreviousStep}
              >
                Back
              </button>
              <button
                type="button"
                className="button-primary w-100"
                onClick={onSubmit}
                disabled={creating}
              >
                {creating ? "Creating..." : "Create Notice"}
              </button>
            </div>
          )}
        </div>
      </div>
      {(creating || isUploading) && <PageLoader />}

      {/* Add New Category Popup */}
      {showCategoryPopup && (
        <div className="cost-details-form-wrapper">
          <div className="license-popup-overlay" onClick={handleCloseCategoryPopup}></div>
          <div className="cost-details-popup" style={{ maxWidth: "600px" }}>
            <div className="popup-header d-flex justify-content-between align-items-start">
              <div>
                <h5 className="mb-2 fw-semibold">Add New Category</h5>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  This action will create a new notice category which will be saved in the category list henceforth.
                </p>
              </div>
              <button
                className="btn-close"
                aria-label="Close"
                onClick={handleCloseCategoryPopup}
              ></button>
            </div>

            <hr className="my-3" />

            <div className="popup-body">
              <div className="mb-3">
                <label className="form-label">
                  New Category Name<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Add new category name here"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            <hr className="my-3" />

            <div className="popup-footer d-flex gap-3">
              <button
                type="button"
                className="button-secondary px-4 w-50"
                onClick={handleCloseCategoryPopup}
                disabled={creatingCategory}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-primary px-4 w-50"
                onClick={handleSaveCategory}
                disabled={creatingCategory}
              >
                {creatingCategory ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddNewNoticesPanel;
